import { getLocale } from 'locales/utils';
import filter from 'lodash/filter';
import isNumber from 'lodash/isNumber';
import * as React from 'react';
import { AdContext, IAdContext, IAdData } from './adContext';
import { CacheContext } from './cacheContext';
import {
  DealerListContext,
  IDealerListContext,
  IDealerListData,
} from './dealerListContext';
import {
  EquipmentListContext,
  IEquipmentListContext,
  IEquipmentListData,
} from './equipmentListContext';
import { GlobalErrorContext } from './globalErrorContext';
import {
  GlobalVideoContext,
  IGlobalVideoContext,
  IGlobalVideoState,
} from './globalVideoContext';
import { InboxContext } from './inboxContext';
import { LayoutContext } from './layoutContext';
import {
  IMaintenanceHistoryContext,
  IMaintenanceHistoryData,
  MaintenanceHistoryContext,
} from './maintenanceHistoryContext';
import { PushNotificationContext } from './pushNotificationContext';
import {
  IShareAccessContext,
  IShareAccessData,
  ShareAccessContext,
} from './shareAccessContext';
import {
  IUserLocation,
  IUserLocationContext,
  UserLocationContext,
} from './userLocationContext';
import {
  NotificationsProvider,
  DialogManagerProvider,
  FirstVisitProvider,
  ManageYourPlanDialogProvider,
  FullscreenStripeLoaderProvider,
} from 'context';
import { isAbortError } from 'errors/isAbortError';
import { AuthProvider } from 'context/useAuth';
import { anonymousAxiosInstance } from 'services/api';
import { GlobalVideo } from 'app/components/GlobalVideo';
import { useNavigate, useParams } from 'react-router-dom';
import { processAndSetData } from 'app/pages/Equipment/EquipmentNotLoaded/utils';

let layoutClassNameIndex = 0;

interface Props {
  children: React.ReactNode;
}

// TODO: Perform a proper refactor of the context provider architecture.
// The `ServiceProviders` and `BusinessLogicProviders` components below are a temporary
// first-pass refactor. They were created by simply splitting the monolithic `ReactContextContainer`
// to improve the separation of concerns. Further work is needed to clean up dependencies
// and streamline the initialization flow.
export default function BusinessLogicProviders({ children }: Props) {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const { addErrorModal } = React.useContext(GlobalErrorContext);

  const [classNames, setClassNames] = React.useState<
    { class: string; index: number }[]
  >([]);

  const [userLocation, setUserLocation] = React.useState({} as IUserLocation);

  const userLocationContextValue = React.useMemo(() => {
    return {
      userLocation: userLocation,
      setUserLocation: setUserLocation,
    };
  }, [userLocation, setUserLocation]) as IUserLocationContext;

  const [equipmentList, setEquipmentList] = React.useState({
    searchValue: '',
  } as IEquipmentListData);

  function optimisticallyRemoveEquipments(ids: string[]) {
    const currentRawEquipment = equipmentList.rawEquipmentList;
    const currentRawGeofence = equipmentList.rawGeofenceList;

    if (!currentRawEquipment || !currentRawGeofence) {
      console.warn('Cannot optimistically update: raw data not initialized.');
      return;
    }

    const newRawEquipmentList = filter(
      currentRawEquipment,
      e => !ids.includes(e.id),
    );

    processAndSetData(
      newRawEquipmentList,
      currentRawGeofence,
      setEquipmentList,
      () => navigate('/equipment'),
      id,
    );
  }

  const equipmentListContextValue = React.useMemo(() => {
    return {
      equipmentListData: equipmentList,
      setEquipmentListData: setEquipmentList,
      optimisticallyRemoveEquipments,
    };
  }, [equipmentList, setEquipmentList]) as IEquipmentListContext;

  const [maintenanceHistory, setMaintenanceHistory] = React.useState(
    {} as IMaintenanceHistoryData,
  );
  const maintenanceHistoryContextValue = React.useMemo(() => {
    return {
      maintenanceHistoryData: maintenanceHistory,
      setMaintenanceHistoryData: setMaintenanceHistory,
    };
  }, [maintenanceHistory, setMaintenanceHistory]) as IMaintenanceHistoryContext;

  const [dealerList, setDealerList] = React.useState({
    listIsLoading: true,
    isMapLoaded: false,
  } as IDealerListData);
  const dealerListContextValue = React.useMemo(() => {
    return {
      dealerListData: dealerList,
      setDealerListData: setDealerList,
    };
  }, [dealerList, setDealerList]) as IDealerListContext;

  const [globalVideoState, setGlobalVideoState] = React.useState({
    video: {
      url: '',
      name: '',
    },
    isModalOpen: false,
    isPiPEnabled: false,
  } as IGlobalVideoState);
  const globalVideoContextValue = React.useMemo(() => {
    return {
      globalVideoStateData: globalVideoState,
      setGlobalVideoStateData: setGlobalVideoState,
    };
  }, [globalVideoState, setGlobalVideoState]) as IGlobalVideoContext;
  const [shareAccess, setShareAccess] = React.useState({} as IShareAccessData);
  const shareAccessContextValue = React.useMemo(() => {
    return {
      shareAccessData: shareAccess,
      setShareAccessData: setShareAccess,
    };
  }, [shareAccess, setShareAccess]) as IShareAccessContext;

  const [adList, setAdList] = React.useState({} as IAdData);
  const adContextValue = React.useMemo(() => {
    return {
      adData: adList,
      setAdData: setAdList,
    };
  }, [adList, setAdList]) as IAdContext;

  const [pushNotifications, setPushNotifications] = React.useState<any>([]);
  const [inbox, setInbox] = React.useState<any>([]);

  const [apiModels, setApiModels] = React.useState<any>(null);
  const [lastLocale, setLastLocale] = React.useState(getLocale());

  const getApiModels = async () => {
    const currentLocale = getLocale();

    if (apiModels != null && currentLocale === lastLocale) {
      return apiModels;
    }

    try {
      const response: any = await anonymousAxiosInstance.get('/api/models');

      if (response.status == 200) {
        const result = {
          categories: response.data?.categories || [],
          models: response.data?.models || [],
        };
        setApiModels(result);

        if (currentLocale !== lastLocale) {
          setLastLocale(currentLocale);
        }

        return result;
      }
    } catch (ex) {
      if (isAbortError(ex)) return;

      addErrorModal();
    }

    return { categories: [], models: [] };
  };

  const removePushNotification = id => {
    setPushNotifications(notifications =>
      notifications.filter(notification => notification !== id),
    );
  };

  const addPushNotification = id => {
    setPushNotifications(pushNotifications.concat(id));
  };

  return (
    <AuthProvider>
      <InboxContext.Provider value={{ inbox, setInbox }}>
        <UserLocationContext.Provider value={userLocationContextValue}>
          <DealerListContext.Provider value={dealerListContextValue}>
            <LayoutContext.Provider
              value={{
                classNames,
                setClassNames: classNames => {
                  const index = layoutClassNameIndex;
                  layoutClassNameIndex += 1;
                  setClassNames(classes =>
                    classes.concat({
                      class: classNames,
                      index: index as number,
                    }),
                  );

                  return index;
                },
                removeClassNames: index => {
                  if (isNumber(index)) {
                    setClassNames(classes =>
                      filter(classes, cls => cls.index !== index),
                    );
                  }
                },
                removeSpecificClass: className => {
                  setClassNames(classes =>
                    filter(classes, cls => cls.class !== className),
                  );
                },
              }}
            >
              <AdContext.Provider value={adContextValue}>
                <FirstVisitProvider>
                  <FullscreenStripeLoaderProvider>
                    <NotificationsProvider>
                      <ShareAccessContext.Provider
                        value={shareAccessContextValue}
                      >
                        <PushNotificationContext.Provider
                          value={{
                            addPushNotification,
                            removePushNotification,
                            pushNotifications,
                          }}
                        >
                          <MaintenanceHistoryContext.Provider
                            value={maintenanceHistoryContextValue}
                          >
                            <EquipmentListContext.Provider
                              value={equipmentListContextValue}
                            >
                              <DialogManagerProvider>
                                <ManageYourPlanDialogProvider>
                                  <GlobalVideoContext.Provider
                                    value={globalVideoContextValue}
                                  >
                                    <GlobalVideo>
                                      <CacheContext.Provider
                                        value={{
                                          getApiModels,
                                          setApiModels,
                                        }}
                                      >
                                        {children}
                                      </CacheContext.Provider>
                                    </GlobalVideo>
                                  </GlobalVideoContext.Provider>
                                </ManageYourPlanDialogProvider>
                              </DialogManagerProvider>
                            </EquipmentListContext.Provider>
                          </MaintenanceHistoryContext.Provider>
                        </PushNotificationContext.Provider>
                      </ShareAccessContext.Provider>
                    </NotificationsProvider>
                  </FullscreenStripeLoaderProvider>
                </FirstVisitProvider>
              </AdContext.Provider>
            </LayoutContext.Provider>
          </DealerListContext.Provider>
        </UserLocationContext.Provider>
      </InboxContext.Provider>
    </AuthProvider>
  );
}
